import type { ReactNode } from "react";

import Check from "@/assets/check.svg";
import { Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { buttonVariants } from "@/components/ui/button.styles";
import { cn } from "@/lib/utils";
import { routes } from "@/routes";

// Bandeau posé en tête d'une page que le club freemium peut utiliser
// normalement, mais dont le résultat reste invisible des joueurs tant qu'il
// n'est pas abonné (ses tournois, par exemple).
export function PremiumBanner({ title, description, action }: { title?: string; description: string; action?: ReactNode }) {
  const { t } = useTranslation(["common"]);

  return (
    <div className="flex flex-col items-center justify-between gap-3 rounded-xl border border-orange-500 bg-orange-100/75 p-4 sm:flex-row">
      <div className="flex items-center gap-3">
        <Lock className="size-5 shrink-0 text-orange-500" />
        <div className="flex flex-col gap-0.5">
          {title && <p className="font-semibold">{title}</p>}
          <p className="text-sm font-medium">{description}</p>
        </div>
      </div>
      {/* Par défaut le bandeau vend l'abonnement. Un club déjà abonné a besoin
          d'une autre action, la publication de son brouillon par exemple. */}
      {action ?? (
        <Link to={routes.plans} className={cn(buttonVariants({ variant: "default" }), "shrink-0")}>
          {t("common:freemium.see-plans")}
        </Link>
      )}
    </div>
  );
}

// Écran de vente cadré, pour une page dont le contenu est vide par nature chez un
// club freemium (ses favoris, par exemple). La carte donne du corps à la page et
// détaille ce que l'abonnement débloque, là où un simple bloc de texte centré
// donne l'impression d'un écran inachevé.
export function PremiumFeatureCard({
  icon,
  title,
  description,
  benefits,
  primaryAction,
  secondaryLabel,
  secondaryTo,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  benefits: string[];
  primaryAction?: ReactNode;
  secondaryLabel?: string;
  secondaryTo?: string;
}) {
  const { t } = useTranslation(["common"]);

  return (
    <div className="container flex justify-center px-2 py-8 md:p-4">
      <div className="flex w-full max-w-lg flex-col items-center gap-5 rounded-[18px] border border-neutral-400 bg-white p-8 text-center">
        <div className="flex items-center justify-center rounded-xl bg-orange-100 p-3 text-orange-500">
          {icon}
        </div>

        <div className="flex flex-col gap-2">
          <h2 className="text-xl font-semibold">{title}</h2>
          <p className="text-balance text-sm text-neutral-600">{description}</p>
        </div>

        <div className="flex w-full flex-col gap-3 rounded-xl bg-neutral-50 p-4 text-left">
          {benefits.map(benefit => (
            <div key={benefit} className="flex items-start gap-2">
              <Check className="mt-0.5 size-4 shrink-0" />
              <span className="text-sm leading-[125%] text-neutral-600">{benefit}</span>
            </div>
          ))}
        </div>

        <div className="flex w-full flex-col items-center gap-3">
          {/* Par défaut la carte vend l'abonnement. Une page que le club freemium
              peut déjà utiliser met en avant son action à elle, la création d'un
              tournoi par exemple, et renvoie vers les abonnements en second. */}
          {primaryAction ?? (
            <Link to={routes.plans} className={cn(buttonVariants({ variant: "default" }), "w-full")}>
              {t("common:freemium.see-plans")}
            </Link>
          )}
          {secondaryLabel && secondaryTo && (
            <Link to={secondaryTo} className="text-sm font-medium text-neutral-500 underline underline-offset-4">
              {secondaryLabel}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

// Écran de vente affiché à un club freemium à la place d'une feature réservée
// aux abonnés (messagerie, détail joueur…).
export function PremiumUpsell({ title, description }: { title: string; description: string }) {
  const { t } = useTranslation(["common"]);

  return (
    <div className="flex w-full flex-col items-center justify-center gap-4 px-4 py-16 text-center">
      <div className="flex items-center justify-center rounded-xl bg-orange-100 p-3">
        <Lock className="size-6 text-orange-500" />
      </div>
      <h2 className="text-xl font-semibold">{title}</h2>
      <p className="max-w-md text-balance text-sm text-neutral-600">{description}</p>
      <Link to={routes.plans} className={cn(buttonVariants({ variant: "default" }))}>
        {t("common:freemium.see-plans")}
      </Link>
    </div>
  );
}
